"use client";

import { useEffect, useRef } from "react";
import useIsMobile from "../../hooks/useIsMobile";

import dynamic from "next/dynamic";

import { isLandscapeAsset } from "../../../sanity/lib/queries/contentBlocks/assetLandscapeQuery";
import { isRelationBlock } from "../../../sanity/lib/queries/contentBlocks/relationBlock";
import { isSlider } from "../../../sanity/lib/queries/contentBlocks/slider";
import { isStackingCardsBlock } from "../../../sanity/lib/queries/contentBlocks/stackingCards";
import { isTextBlock } from "../../../sanity/lib/queries/contentBlocks/textBlock";
import { isTextListBlock } from "../../../sanity/lib/queries/contentBlocks/textListBlock";
import { isImageCollage } from "../../../sanity/lib/types/imageCollage";
import { isImageTextCollage } from "../../../sanity/lib/types/imageTextCollage";
import { isRichTextBlock } from "../../../sanity/lib/types/richTextBlock";
import { isSolutionGrid } from "../../../sanity/lib/types/solutionGrid";
import { isTextTriad } from "../../../sanity/lib/types/textTriad";
import { isLogoSlider } from "../../../sanity/lib/queries/contentBlocks/logoSlider";
import { isServiceListBlock } from "../../../sanity/lib/queries/contentBlocks/serviceListBlock";
import { isFaqSection } from "../../../sanity/lib/types/faqSection";
import { isContactForm } from "../../../sanity/lib/types/contactForm";
import { isTestimonialSwiper } from "../../../sanity/lib/queries/contentBlocks/testimonialSwiper";

import { Page } from "../../../sanity/lib/queries/getPageData";

import { ImageCollage } from "../layoutModules/ImageCollage";
import { ImageCollageMobile } from "../layoutModules/ImageCollageMobile";
import { ImageTextCollage } from "../layoutModules/ImageTextCollage";
import { LandscapeAsset } from "../layoutModules/LandscapeAsset";
import { RelationBlockModule } from "../layoutModules/RelationBlockModule";
import { RichTextBlock } from "../layoutModules/RichTextBlock";
import { TextBlock } from "../layoutModules/TextBlock";
import { TextListSection } from "../layoutModules/TextListSection";
import { TextTriad } from "../layoutModules/TextTriad";
import { SolutionGrid } from "../solution/solutionGrid";
import { FaqBlock } from "../layoutModules/FaqBlock";
import { ContactForm } from "../layoutModules/ContactForm";
import { useAppContext } from "../../provider/AppProvider";

const Slider = dynamic(() => import("../layoutModules/Slider"));
const TestimonialSwiper = dynamic(() => import("../layoutModules/TestimonialSwiper"));
const LogoSlider = dynamic(() => import("../layoutModules/LogoSlider"));
const ServiceListBlock = dynamic(() => import("../layoutModules/ServiceListBlock"));
const StackingCards = dynamic(() => import("../layoutModules/StackingCards"));

export const PageContent = ({ pageData }: { pageData: Page }) => {
    const { isMobile } = useIsMobile();
    const pagecontent = useRef<HTMLDivElement>(null);
    const observer = useRef<IntersectionObserver>(null);
    const { setIsInView } = useAppContext();

    useEffect(() => {
        const observerOptions = {
            root: null,
            rootMargin: "100px 0px 100px 0px",
            threshold: 0.01,
        };

        observer.current = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsInView(entry.isIntersecting);
            }
        }, observerOptions);
    }, []);

    useEffect(() => {
        if (pagecontent.current) {
            observer.current?.observe(pagecontent.current);
        }
        return () => {
            observer.current?.disconnect();
        };
    }, []);

    return (
        <div ref={pagecontent}>
            {pageData.content?.map((block, index) => {
                if (isSolutionGrid(block)) {
                    return (
                        <SolutionGrid
                            key={index}
                            solutions={block.solutions || []}
                            filterData={block.filterData || { services: [], industries: [] }}
                            label={block.filterLabel}
                            serviceLabel={block.serviceLabel}
                            industryLabel={block.industryLabel}
                            emptyStateMessage={block.emptyStateMessage}
                            emptyShowcaseSolutions={block.emptyShowcaseSolutions?.slice(0, 3) || []}
                        />
                    );
                }
                if (isLogoSlider(block)) {
                    return <LogoSlider key={index} slider={block} />;
                }
                if (isRelationBlock(block)) {
                    return <RelationBlockModule key={index} relationBlock={block} />;
                }
                if (isTestimonialSwiper(block)) {
                    return <TestimonialSwiper key={index} slider={block} />;
                }
                if (isTextListBlock(block)) {
                    return <TextListSection key={index} textSection={block} />;
                }
                if (isTextTriad(block)) {
                    return <TextTriad key={index} {...block} />;
                }
                if (isLandscapeAsset(block)) {
                    return (
                        <div key={index} className="container">
                            <div className="mx-2.5 md:mx-5">
                                <LandscapeAsset asset={block} spacing="small" />
                            </div>
                        </div>
                    );
                }
                if (isTextBlock(block)) {
                    return <TextBlock key={index} data={{ ...block, margin: true }} />;
                }
                if (isStackingCardsBlock(block)) {
                    return <StackingCards key={index} {...block} />;
                }
                if (isSlider(block)) {
                    return <Slider key={index} slider={block} margin={true} />;
                }
                if (isImageCollage(block)) {
                    if (isMobile) {
                        return <ImageCollageMobile key={index} mobileImages={block.mobileImages} />;
                    } else {
                        return <ImageCollage key={index} images={block.images} />;
                    }
                }
                if (isImageTextCollage(block)) {
                    return <ImageTextCollage key={index} {...block} />;
                }
                if (isRichTextBlock(block)) {
                    return <RichTextBlock key={index} content={block} />;
                }
                if (isServiceListBlock(block)) {
                    return <ServiceListBlock key={index} listBlock={block} />;
                }
                if (isFaqSection(block)) {
                    return <FaqBlock key={index} data={block} />;
                }
                if (isContactForm(block)) {
                    return <ContactForm key={index} data={block} />;
                }
            })}
        </div>
    );
};
